import { ChangeDetectorRef, Component, inject, Input, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatFormField, MatLabel, MatOption, MatSelect } from '@angular/material/select'
import { TranslateModule } from '@ngx-translate/core'
import { ToastService } from '../toast.service'
import { Call, CallResourceType, CallsService } from '../calls.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { DocumentUploadInfo } from '../utils'
import { FileUploadType } from '../uploads.service'
import { environment } from '../../environments/environment'

export type UserUploadedResource = {
  type: CallResourceType
  extensions: string[]
  maxSize: number | null
  uploadType: FileUploadType
}

const MAX_AGENDA_SIZE_IN_BYTES = 2_000_000
export const MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES = 10_000_000

export const callResourceTypesToUpload: { [type: string]: UserUploadedResource } = {
  AGENDA: { type: 'AGENDA', extensions: ['pdf', 'docx'], maxSize: MAX_AGENDA_SIZE_IN_BYTES, uploadType: 'agenda' },
}

export const resourcesAcceptedFromUser = Object.values(callResourceTypesToUpload)

export function getMissingDocumentsForCall(call: Call) {
  return resourcesAcceptedFromUser.filter(
    (document) => document.type === 'CUSTOM_DOCUMENT' || !call.userUploadedResources.map((resource) => resource.resourceType).includes(document.type)
  )
}

@Component({
  selector: 'app-call-document-upload',
  standalone: true,
  imports: [MatSelect, TranslateModule, MatOption, FormsModule, MatButton, MatLabel, MatFormField, SpinnerComponent, FileUploadComponent],
  templateUrl: './call-document-upload.component.html',
  styleUrl: './call-document-upload.component.scss',
})
export class CallDocumentUploadComponent {
  @Input({ required: true })
  callId!: number

  @Input({ required: true })
  acceptedDocuments!: UserUploadedResource[]

  @ViewChild(FileUploadComponent)
  fileUploadComponent!: FileUploadComponent

  callsService = inject(CallsService)
  toastService = inject(ToastService)
  changeDetectorRef = inject(ChangeDetectorRef)
  selectedDocumentType: string | null = null
  acceptedExtensions: string[] = []
  maxSize: number | null = null
  importResourceMutation = this.callsService.importResource()
  uploading = false
  uploadInfo: DocumentUploadInfo | null = null

  get showCustomDocuments(): boolean {
    return environment.showCustomDocuments
  }

  onDocumentTypeChange() {
    const document = this.acceptedDocuments.find((doc) => doc.type === this.selectedDocumentType) ?? null
    this.acceptedExtensions = document !== null ? document.extensions : []
    this.maxSize = document !== null ? document.maxSize : null
    if (this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.removeFile()
    }
  }

  async onUploadClick() {
    this.uploading = true
    try {
      await this.importResourceMutation.mutateAsync({
        callId: this.callId,
        data: {
          fileType: this.selectedDocumentType!,
          fileExtension: this.uploadInfo!.extension,
          uploadKey: this.uploadInfo!.key,
          fileName: this.uploadInfo!.fileName,
        },
      })
    } catch (error) {
      console.error('Failed to upload document:', error)
    } finally {
      this.uploading = false
      this.uploadInfo = null
      this.selectedDocumentType = null
      this.changeDetectorRef.detectChanges()
    }
  }

  getUploadType(selectedDocumentType: string) {
    return callResourceTypesToUpload[selectedDocumentType].uploadType
  }
}
